import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiGet, apiPost, apiDelete, api } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import EmptyState from "@/components/common/EmptyState";
import PortfolioSetup from "@/components/portfolio/PortfolioSetup";
import PortfolioSuggestion from "@/components/portfolio/PortfolioSuggestion";
import PortfolioDashboard from "@/components/portfolio/PortfolioDashboard";
import PortfolioSelectorBar from "@/components/portfolio/PortfolioSelectorBar";
import PortfolioCombinedView from "@/components/portfolio/PortfolioCombinedView";
import UsPortfolioPanel from "@/components/portfolio/UsPortfolioPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Briefcase, Plus, Sparkles, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const LS_ACTIVE_KEY = "cse-active-portfolio";
const LS_MARKET_KEY = "portfolio-active-market"; // CSE | US

// ---- Side-by-side never-merged totals header card ------------------------

const fmtLkr = (v) => v == null ? "—" : `Rs. ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtUsd = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MarketTotalsHeader = ({ data }) => {
  if (!data) return null;
  const cse = data.cse || {};
  const us = data.us || {};
  const showUs = data.us_stocks_enabled && (us.portfolios_count || 0) >= 0;
  return (
    <Card data-testid="market-totals-header">
      <CardContent className="pt-4 pb-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {/* CSE totals */}
          <div className="flex-1 min-w-[12rem]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base">🇱🇰</span>
              <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">CSE Stocks</span>
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-xl font-semibold tabular-nums" data-testid="market-totals-cse-current">
                {fmtLkr(cse.current_value)}
              </span>
              {cse.gain_loss_pct != null ? (
                <span className={`text-xs tabular-nums ${cse.gain_loss_pct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                  {cse.gain_loss_pct >= 0 ? "+" : ""}{Number(cse.gain_loss_pct).toFixed(1)}%
                </span>
              ) : null}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Invested {fmtLkr(cse.invested_amount)} · {cse.holdings_count || 0} holdings
            </div>
          </div>

          {/* Vertical divider */}
          {showUs ? <div className="hidden sm:block w-px h-12 bg-border" /> : null}

          {/* US totals */}
          {showUs ? (
            <div className="flex-1 min-w-[12rem]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">🇺🇸</span>
                <span className="text-xs uppercase tracking-wider font-medium text-muted-foreground">US Stocks</span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-xl font-semibold tabular-nums" data-testid="market-totals-us-current">
                  {fmtUsd(us.current_value_usd)}
                </span>
                {us.gain_loss_pct != null ? (
                  <span className={`text-xs tabular-nums ${us.gain_loss_pct >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                    {us.gain_loss_pct >= 0 ? "+" : ""}{Number(us.gain_loss_pct).toFixed(1)}%
                  </span>
                ) : null}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Invested {fmtUsd(us.invested_amount_usd)} · {us.holdings_count || 0} holdings
              </div>
            </div>
          ) : null}

          {/* Footer note: never merged */}
          <div className="ml-auto text-[10px] text-muted-foreground italic max-w-[14rem] text-right">
            Markets are tracked separately. No currency conversion.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PortfolioPage = () => {
  const { isAdmin, user } = useAuth();
  const showUsTab = !!(isAdmin || (user && user.us_stocks_enabled));
  const [marketTab, setMarketTab] = useState(() => {
    if (!showUsTab) return "CSE";
    return localStorage.getItem(LS_MARKET_KEY) || "CSE";
  });
  useEffect(() => { localStorage.setItem(LS_MARKET_KEY, marketTab); }, [marketTab]);

  const [marketTotals, setMarketTotals] = useState(null);
  const loadMarketTotals = useCallback(async () => {
    try {
      const d = await apiGet("/me/portfolios-by-market");
      setMarketTotals(d);
    } catch {
      setMarketTotals(null);
    }
  }, []);

  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(LS_ACTIVE_KEY) || null);
  const [summary, setSummary] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Phase 9 Fix 5 — Read initial view from URL `?view=suggestion` so the
  // Stock Detail back button can return us straight to the checkbox flow.
  const [view, setView] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("view");
      if (v === "suggestion" || v === "dashboard") return v;
    } catch (_e) { /* ignore */ }
    return "dashboard";
  });
  // Strip ?view= from the URL after we apply it so a refresh doesn't pin
  // the view forever.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has("view")) {
        params.delete("view");
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
        window.history.replaceState({}, "", newUrl);
      }
    } catch (_e) { /* ignore */ }
  }, []);
  const [creating, setCreating] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [forcedView, setForcedView] = useState(null);

  // Persist active portfolio
  useEffect(() => {
    if (activeId) localStorage.setItem(LS_ACTIVE_KEY, activeId);
  }, [activeId]);

  const isAll = activeId === "all";

  const loadList = useCallback(async () => {
    const list = await apiGet("/portfolios");
    setPortfolios(list);
    return list;
  }, []);

  const loadSummary = useCallback(async (id) => {
    if (id === "all") {
      const agg = await apiGet("/portfolios/aggregate");
      setAggregate(agg);
      setSummary(null);
      return;
    }
    const s = await apiGet(`/portfolios/${id}/summary`);
    setSummary(s);
    setAggregate(null);
  }, []);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        loadMarketTotals();
        const list = await loadList();
        if (cancelled) return;
        if (list.length === 0) {
          setActiveId(null);
          setSummary(null);
          setAggregate(null);
          if (marketTab === "CSE") setSetupOpen(true);
        } else {
          let chosen = activeId;
          // Validate persisted id still exists, fall back to first
          if (chosen !== "all" && !list.some((p) => p.portfolio_id === chosen)) {
            chosen = list[0].portfolio_id;
          }
          setActiveId(chosen);
          await loadSummary(chosen);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When activeId changes after first load, refetch
  useEffect(() => {
    if (loading) return;
    if (!activeId) return;
    loadSummary(activeId)
      .then(() => {
        if (forcedView) {
          setView(forcedView);
          setForcedView(null);
        } else {
          setView("dashboard");
        }
      })
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const refreshActive = async () => {
    if (!activeId) return;
    await loadSummary(activeId);
  };

  const handleCreate = async (prefs) => {
    setCreating(true);
    try {
      const created = await apiPost("/portfolios", { ...prefs });
      toast.success("Portfolio created. AI suggestion ready.");
      const list = await loadList();
      setPortfolios(list);
      setForcedView("suggestion");
      setActiveId(created.portfolio_id);
      setSetupOpen(false);
    } catch (e) {
      toast.error("Failed to create portfolio");
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async (prefs) => {
    if (!activeId || isAll) return;
    try {
      await api.put(`/portfolios/${activeId}`, prefs);
      toast.success("Preferences updated");
      await loadList();
      await loadSummary(activeId);
      setEditOpen(false);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    const target = portfolios.find((p) => p.portfolio_id === id);
    if (!target) return;
    const ok = window.confirm(
      `Are you sure you want to delete "${target.portfolio_name}"?\n\nThis will also delete all holdings and dividend records for this portfolio. This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await apiDelete(`/portfolios/${id}`);
      toast.success("Portfolio deleted");
      const list = await loadList();
      if (list.length === 0) {
        setActiveId(null);
        setSummary(null);
        setAggregate(null);
        setSetupOpen(true);
      } else {
        setActiveId(list[0].portfolio_id);
      }
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const regenerateSuggestion = async () => {
    if (!activeId || isAll) return;
    try {
      const updated = await apiPost(`/portfolios/${activeId}/regenerate-suggestion`);
      // refresh summary so summary.portfolio.ai_suggestion reflects new
      await loadSummary(activeId);
      setView("suggestion");
      toast.success("AI suggestion refreshed");
    } catch (e) {
      toast.error("Failed to regenerate");
    }
  };

  if (loading) return <LoadingState label="Loading portfolios…" />;
  if (error) return <ErrorState description={error} />;

  const cseContent = (
    <>
      <PortfolioSelectorBar
        portfolios={portfolios}
        activeId={activeId}
        onChange={setActiveId}
        onNew={() => setSetupOpen(true)}
        onEdit={() => setEditOpen(true)}
        onDelete={() => activeId && !isAll && handleDelete(activeId)}
        onViewSuggestion={() => setView("suggestion")}
        onViewDashboard={() => setView("dashboard")}
        view={view}
        canEdit={!!activeId && !isAll}
      />

      {/* Empty state */}
      {portfolios.length === 0 && !setupOpen && (
        <EmptyState
          icon={Briefcase}
          title="No CSE portfolios yet"
          description="Create your first portfolio and Claude will suggest an allocation."
          action={
            <Button onClick={() => setSetupOpen(true)} className="gap-2" data-testid="create-first-portfolio-button">
              <Plus className="h-4 w-4" /> Create portfolio
            </Button>
          }
        />
      )}

      {/* Combined view */}
      {isAll && aggregate && (
        <PortfolioCombinedView
          aggregate={aggregate}
          onSelectPortfolio={(id) => setActiveId(id)}
        />
      )}

      {/* Single portfolio dashboard / suggestion */}
      {!isAll && summary && view === "dashboard" && (
        <PortfolioDashboard summary={summary} onChange={refreshActive} />
      )}
      {!isAll && summary && view === "suggestion" && (
        <PortfolioSuggestion
          portfolio={summary.portfolio}
          onContinue={() => setView("dashboard")}
          onRegenerate={regenerateSuggestion}
        />
      )}
    </>
  );

  return (
    <div className="space-y-5" data-testid="portfolio-page">
      {/* Never-merged side-by-side market totals */}
      <MarketTotalsHeader data={marketTotals} />

      {showUsTab ? (
        <Tabs value={marketTab} onValueChange={setMarketTab} className="space-y-4" data-testid="portfolio-market-tabs">
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="CSE" data-testid="portfolio-tab-cse">
              <span className="mr-1.5">🇱🇰</span> CSE Stocks
            </TabsTrigger>
            <TabsTrigger value="US" data-testid="portfolio-tab-us">
              <span className="mr-1.5">🇺🇸</span> US Stocks
            </TabsTrigger>
          </TabsList>
          <TabsContent value="CSE" className="space-y-4">{cseContent}</TabsContent>
          <TabsContent value="US" className="space-y-4">
            <UsPortfolioPanel />
          </TabsContent>
        </Tabs>
      ) : (
        <>{cseContent}</>
      )}

      {/* New portfolio modal */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent
          className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="new-portfolio-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-5 w-5 text-primary" /> New CSE portfolio
            </DialogTitle>
          </DialogHeader>
          <PortfolioSetup
            initial={{ portfolio_name: "", investment_amount: 1000000 }}
            onSubmit={handleCreate}
            submitting={creating}
            allowDelete={false}
            embedded
          />
        </DialogContent>
      </Dialog>

      {/* Edit portfolio modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="edit-portfolio-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display">Edit preferences</DialogTitle>
          </DialogHeader>
          <PortfolioSetup
            initial={summary?.portfolio || {}}
            onSubmit={handleEdit}
            submitting={false}
            allowDelete={false}
            embedded
            submitLabel="Save changes"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioPage;
